import React from 'react'
import PropTypes from 'prop-types'
import { SPECIALIST_ASSISTANTS } from '../data/assistants'
import PublicNav from './PublicNav'
import Footer from './Footer'

const FeaturesPage = ({ onBack, onLogin, onShowPricing, onShowAbout, onShowContact, onShowPrivacy, onShowTerms, isActive = false }) => {
  const basePath = import.meta.env.BASE_URL

  const features = [
    {
      id: 'natural-language',
      title: (<><span className="text-sky-300">Natural language</span> idea development</>),
      subtitle: 'No forms or checklists - Chat naturally with your custom team of AI specialists to validate and grow your ideas',
      description: 'Start by chatting with Auri, your personal guide. She\'ll understand what you\'re trying to build and connect you with the right team of specialists to move your idea forward.',
      color: 'from-sky-500 to-sky-600',
      icon: <path d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" strokeLinecap="round" strokeLinejoin="round" />,
      screenshot: `${import.meta.env.BASE_URL}images/features/auri.png`,
    },
  ]

  return (
    <div className="min-h-screen bg-gray-950">
      <PublicNav 
        onShowFeatures={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onShowPricing={onShowPricing}
        onLogin={onLogin}
        onBack={onBack}
        activeProduct={true}
      />

      <header className="pt-24 pb-20 relative z-10 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-display text-5xl font-light tracking-tight text-white sm:text-7xl mb-6">
              Develop your ideas with a custom team of <span className="text-sky-300">AI specialists</span>
            </h1>
            <p className="mt-8 text-2xl/10 text-gray-300 max-w-3xl mx-auto">
              Work together to validate, develop, and launch your ideas through natural conversation, not forms and checklists
            </p>
          </div>
        </div>
      </header>

      <main>
        {features.map((feature, idx) => (
          <React.Fragment key={feature.id}>
            <div id="natural-language" className={idx % 2 === 0 ? 'bg-gray-900/30' : ''}>
              <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
                <div 
                  className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 items-start`}
                >
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-2">
                      {feature.title}
                    </h2>
                    <p className="text-xl text-sky-300 mb-4">{feature.subtitle}</p>
                    <p className="text-xl text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                  <div className="flex-1 w-full">
                    {feature.screenshot ? (
                      <img 
                        src={feature.screenshot} 
                        alt={`${feature.title} screenshot`}
                        className="rounded-xl shadow-2xl border border-white/10"
                      />
                    ) : (
                      <div className="aspect-video rounded-xl bg-gray-800/50 border border-white/10 flex items-center justify-center">
                        <span className="text-gray-500 text-sm">Screenshot coming soon</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {idx === 0 && (
              <>
              <div id="team-assistants" className="py-24">
                <div className="mx-auto max-w-4xl text-center mb-20"><h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">Your team of <span className="text-sky-300">specialists</span>, at a fraction of the cost</h2></div>
                <div className="mx-auto max-w-5xl mb-20 px-4 sm:px-6 lg:px-8">
                  <div className="relative rounded-3xl bg-gradient-to-br from-sky-800/40 via-sky-700/30 to-sky-900/40 shadow-2xl outline -outline-offset-1 outline-sky-400/40 p-12 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-300/60 to-transparent pointer-events-none"></div>
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-sky-500/10 via-transparent to-transparent rounded-3xl pointer-events-none"></div>
                    <div className="absolute -top-20 -right-20 w-80 h-80 bg-sky-400/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-sky-600/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="relative z-10 flex flex-col lg:flex-row items-center gap-12">
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="w-64 h-64 flex items-center justify-center">
                            <img src={`${basePath}characters/Auri/1_Default/1_wave.png`} alt="Auri" className="w-full h-full object-contain drop-shadow-2xl" style={{imageRendering: "crisp-edges"}} />
                          </div>
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-sky-400 text-gray-900 text-sm font-bold px-4 py-2 rounded-full shadow-lg">YOUR GUIDE</div>
                        </div>
                      </div>
                      <div className="flex-1 text-center lg:text-left">
                        <h3 className="font-display text-4xl font-light tracking-tight text-white mb-3">Auri</h3>
                        <p className="text-xl text-sky-300 font-medium mb-4">Your guide and team builder</p>
                        <p className="text-lg text-gray-300 leading-relaxed">For each idea you have, she'll assemble the perfect AI team to take it forward. She'll connect you with the right specialists at exactly the right time.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mx-auto max-w-3xl text-center mb-28"><h3 className="font-display text-4xl font-light tracking-tight text-white mb-4"><span className="text-sky-300">The specialists</span></h3><p className="text-xl text-gray-400">Auri will connect you with these experts as your idea progresses</p></div>
                <div className="mx-auto max-w-5xl"><div className="grid grid-cols-1 gap-24 sm:gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 mb-8">{SPECIALIST_ASSISTANTS.slice(0, 3).map((assistant) => (<div key={assistant.id} id={`assistant-${assistant.id}`} className="relative rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 pt-40 sm:pt-32 pb-6 px-6 text-center"><div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div><div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div><div className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 h-48 flex items-center justify-center"><img alt={assistant.name} className="w-full h-full object-contain drop-shadow-2xl" src={assistant.poses.default} style={{imageRendering: "crisp-edges"}} /></div><h3 className="text-xl font-semibold text-white mb-2 relative z-10">{assistant.name}</h3><p className="text-base text-sky-300 mb-2 relative z-10">{assistant.title}</p><p className="text-base text-gray-400 relative z-10">{assistant.subtitle}</p></div>))}</div><div className="grid grid-cols-1 gap-24 sm:gap-8 sm:grid-cols-2 md:grid-cols-2 max-w-3xl mx-auto mt-24 sm:mt-24">{SPECIALIST_ASSISTANTS.slice(3).map((assistant) => (<div key={assistant.id} id={`assistant-${assistant.id}`} className="relative rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 pt-40 sm:pt-32 pb-6 px-6 text-center"><div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div><div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div><div className="absolute -top-20 left-1/2 -translate-x-1/2 w-48 h-48 flex items-center justify-center"><img alt={assistant.name} className="w-full h-full object-contain drop-shadow-2xl" src={assistant.poses.default} style={{imageRendering: "crisp-edges"}} /></div><h3 className="text-xl font-semibold text-white mb-2 relative z-10">{assistant.name}</h3><p className="text-base text-sky-300 mb-2 relative z-10">{assistant.title}</p><p className="text-base text-gray-400 relative z-10">{assistant.subtitle}</p></div>))}</div></div>
              </div>
              <div id="structured-analysis" className="bg-gray-900/30">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 text-center">
                  <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">Structured analysis & feedback, <span className="text-sky-300">without the forms</span></h2>
                  <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto mb-8">
                    Each specialist guides you through industry-proven methodologies—but through natural conversation, not forms and checklists. You&apos;ll receive structured assessments with clear reasoning, actionable insights, and specific recommendations to strengthen your venture.
                  </p>
                  <img 
                    src={`${basePath}images/features/structured_analysis.png`}
                    alt="Structured analysis and feedback"
                    className="rounded-xl shadow-2xl border border-white/10 w-full"
                  />
                </div>
              </div>
              <div id="interactive-canvas">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 text-center">
                  <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">Interactive <span className="text-sky-300">canvas</span></h2>
                  <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto mb-8">
                    Collaborate with your AI team on a dynamic whiteboard where ideas come to life. Brainstorm, organize, and iterate visually. Connect concepts, add notes, and see your idea evolve in real-time as you work through the development process.
                  </p>
                  <img 
                    src={`${basePath}images/features/canvas.png`}
                    alt="Interactive canvas whiteboard"
                    className="rounded-xl shadow-2xl border border-white/10 w-full"
                  />
                </div>
              </div>
              <div id="task-management" className="bg-gray-900/30">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 text-center">
                  <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">Comprehensive <span className="text-sky-300">task management</span></h2>
                  <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto mb-8">
                    Stay organized from idea to execution with AI-suggested next steps and milestones. Never lose momentum with intelligent task recommendations based on your current stage. See clear progress indicators and know exactly what to focus on next.
                  </p>
                  <img 
                    src={`${basePath}images/features/tasks.png`}
                    alt="Task management interface"
                    className="rounded-xl shadow-2xl border border-white/10 w-full"
                  />
                </div>
              </div>
              <div id="reports-insights">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 text-center">
                  <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">Detailed <span className="text-sky-300">reports & insights</span></h2>
                  <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto mb-8">
                    Access comprehensive reports from each AI specialist with detailed analysis, recommendations, and actionable insights. Export and share your findings to keep stakeholders informed and aligned on your venture&apos;s progress.
                  </p>
                  <img 
                    src={`${basePath}images/features/reports.png`}
                    alt="Reports and insights interface"
                    className="rounded-xl shadow-2xl border border-white/10 w-full"
                  />
                </div>
              </div>
              <div id="unified-dashboard" className="bg-gray-900/30">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 text-center">
                  <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-6">Unified <span className="text-sky-300">dashboard</span></h2>
                  <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto mb-8">
                    Manage all your ideas from a single, intuitive dashboard. Track progress across multiple ventures, see status updates at a glance, and quickly access any idea to continue working with your AI team.
                  </p>
                  <img 
                    src={`${basePath}images/features/dashboard.png`}
                    alt="Opptora dashboard interface"
                    className="rounded-xl shadow-2xl border border-white/10 w-full"
                  />
                </div>
              </div>
              <div id="market-research">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
                  <div className="flex flex-col md:flex-row-reverse gap-8 items-center md:items-start">
                    <div className="flex-1 text-center md:text-left">
                      <h2 className="font-display text-4xl font-light tracking-tight text-white sm:text-5xl mb-2">Transparent <span className="text-sky-300">market research</span></h2>
                      <p className="text-lg text-sky-300 mb-4">See exactly how your AI team works</p>
                      <p className="text-xl text-gray-400 leading-relaxed">
                        No black boxes here. See the exact search terms your AI team generates, review the sources they find, and understand how insights are extracted. Work alongside your AI specialists with full visibility into every step of the research process.
                      </p>
                    </div>
                    <div className="flex-1 w-full">
                      <img 
                        src={`${basePath}images/features/research.png`}
                        alt="Market research and analysis"
                        className="rounded-xl shadow-2xl border border-white/10"
                      />
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}
          </React.Fragment>
        ))}
      </main>

      <div className="bg-gray-900/50 py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-light tracking-tight text-white mb-4">
            Ready to bring your idea to life?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Join Opptora and start validating your business ideas today.
          </p>
          <button 
            onClick={() => onLogin('register')}
            className="rounded-md bg-gradient-to-br from-blue-500/60 to-blue-600/40 px-8 py-3.5 text-lg font-semibold text-white hover:from-blue-500/50 hover:to-blue-600/30 transition-all shadow-lg"
          >
            Get Started Free
          </button>
        </div>
      </div>

      <Footer variant="public" onShowFeatures={() => window.scrollTo({ top: 0, behavior: 'smooth' })} onShowPricing={onShowPricing} onShowAbout={onShowAbout} onShowContact={onShowContact} onShowPrivacy={onShowPrivacy} onShowTerms={onShowTerms} />
    </div>
  )
}

FeaturesPage.propTypes = {
  onBack: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onShowPricing: PropTypes.func.isRequired,
  onShowAbout: PropTypes.func,
  onShowContact: PropTypes.func,
  onShowPrivacy: PropTypes.func,
  onShowTerms: PropTypes.func,
  isActive: PropTypes.bool,
}

export default FeaturesPage
